// Chart of accounts: the list of accounts the books use, grouped as the
// financial statements show them. Changes go through acc_create_account /
// acc_update_account; the database refuses anything that would break history.
import { useState, useMemo, useEffect } from "react";
import { Modal, Button, Field, Input, Select, TextArea, Notice, ActionMenu } from "./kit/components";
import { T, text } from "./kit/tokens";
import { showToast } from "../ui/core";
import { createAccount, updateAccount } from "./api";
import { SectionCard, Chip, TYPE_INFO, CASH_FLOW, cashFlowLabel, errText } from "./shared";

const ORDER = ["asset", "liability", "equity", "income", "expense"];
const RENT_PENDING = ["1220", "4200", "4210"];   // not used until the rent-receipt treatment is decided (D6)

export default function ChartOfAccounts({ ctx }) {
    const { data, reload, mobile } = ctx;
    const [q, setQ] = useState("");
    const [showInactive, setShowInactive] = useState(true);
    const [adding, setAdding] = useState(null);     // parent account for a new account
    const [editing, setEditing] = useState(null);
    const parties = useMemo(() => Object.fromEntries(data.parties.map(p => [p.id, p])), [data.parties]);
    const mapped = useMemo(() => new Set(data.mappings.map(m => m.account_id)), [data.mappings]);

    // depth-first order so sub-accounts sit under their heading
    const tree = useMemo(() => {
        const kids = {};
        data.accounts.forEach(a => { (kids[a.parent_id || "root"] ||= []).push(a); });
        Object.values(kids).forEach(list => list.sort((a, b) => a.code.localeCompare(b.code)));
        const out = [];
        const walk = (a, depth) => { out.push({ ...a, depth }); (kids[a.id] || []).forEach(k => walk(k, depth + 1)); };
        (kids.root || []).forEach(a => walk(a, 0));
        return out;
    }, [data.accounts]);

    const needle = q.trim().toLowerCase();
    const visible = tree.filter(a => (showInactive || a.active) && (!needle || `${a.code} ${a.name}`.toLowerCase().includes(needle)));

    return (
        <div>
            <Notice tone="info">This is the draft chart for your auditor to review. Accounts that are used by automatic postings are marked <b>Built-in</b> and can be renamed but not removed. An account that already has postings keeps its type and settings for good.</Notice>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", margin: "16px 0" }}>
                <Input type="search" placeholder="Search code or name…" value={q} onChange={e => setQ(e.target.value)} style={{ flex: mobile ? "1 1 100%" : "0 1 260px" }} />
                <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13, color: T.textMuted, cursor: "pointer" }}>
                    <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} /> Show inactive accounts
                </label>
            </div>
            {ORDER.map(type => {
                const rows = visible.filter(a => a.type === type);
                if (!rows.length) return null;
                const root = data.accounts.find(a => a.type === type && !a.parent_id);
                return (
                    <SectionCard key={type} title={TYPE_INFO[type][0]} subtitle={TYPE_INFO[type][1]} pad={0}
                        actions={root && <Button size="sm" variant="secondary" onClick={() => setAdding(root)}>Add account</Button>}>
                        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                            {rows.map(a => {
                                const header = !a.is_postable;
                                return (
                                    <li key={a.id} style={{ display: "flex", gap: 10, alignItems: "center", padding: "10px 16px",
                                        borderBottom: `1px solid ${T.borderSoft}`, opacity: a.active ? 1 : 0.6 }}>
                                        <span style={{ fontFamily: "ui-monospace, Consolas, monospace", fontSize: 13, color: T.textMuted, width: 44, flexShrink: 0 }}>{a.code}</span>
                                        <div style={{ flex: 1, minWidth: 0, paddingLeft: a.depth * (mobile ? 10 : 20) }}>
                                            <div style={{ color: T.textStrong, fontWeight: header ? 700 : 500, fontSize: 14, overflowWrap: "anywhere" }}>{a.name}</div>
                                            <div style={{ marginTop: 3 }}>
                                                {header && <Chip>Heading</Chip>}
                                                {a.is_system && <Chip tone={T.primary}>Built-in</Chip>}
                                                {mapped.has(a.id) && !a.is_system && <Chip tone={T.primary}>Used by automation</Chip>}
                                                {a.is_contra && <Chip>Reduces its group</Chip>}
                                                {a.fixed_party_id ? <Chip tone={T.info}>Only for {parties[a.fixed_party_id]?.name || "one party"}</Chip>
                                                    : a.requires_party && <Chip tone={T.info}>Tracked per person/company</Chip>}
                                                {!a.active && <Chip tone={T.warning}>Inactive{RENT_PENDING.includes(a.code) ? " · waiting for the rent decision" : ""}</Chip>}
                                                {!header && a.cash_flow_class && !mobile && <span style={text.caption}>Cash flow: {cashFlowLabel(a.cash_flow_class)}</span>}
                                            </div>
                                            {a.description && <div style={{ ...text.caption, marginTop: 3 }}>{a.description}</div>}
                                        </div>
                                        <ActionMenu items={[
                                            { label: "Edit", onClick: () => setEditing(a) },
                                            header && { label: "Add sub-account", onClick: () => setAdding(a) },
                                        ]} />
                                    </li>
                                );
                            })}
                        </ul>
                    </SectionCard>
                );
            })}
            <AddAccountModal open={!!adding} parentDefault={adding} ctx={ctx} onClose={() => setAdding(null)} onDone={async () => { setAdding(null); await reload(); }} />
            <EditAccountModal account={editing} onClose={() => setEditing(null)} onDone={async () => { setEditing(null); await reload(); }} />
        </div>
    );
}

// Suggests the next unused code inside a heading's range: 2410 → 2411…2419, 1300 → 1310, 1320…, 1000 → 1100, 1200…
export function nextFreeCode(accounts, parent) {
    const used = new Set(accounts.map(a => a.code));
    const base = Number(parent.code);
    const zeros = parent.code.match(/0*$/)[0].length;
    const span = 10 ** Math.max(zeros, 1);
    const step = zeros >= 2 ? 10 ** (zeros - 1) : 1;
    for (let c = base + step; c < base + span; c += step) if (!used.has(String(c))) return String(c);
    for (let c = base + 1; c < base + span; c++) if (!used.has(String(c))) return String(c);
    return "";
}

function AddAccountModal({ open, parentDefault, ctx, onClose, onDone }) {
    const { data } = ctx;
    const headers = data.accounts.filter(a => !a.is_postable);
    const [parentId, setParentId] = useState("");
    const [code, setCode] = useState("");
    const [name, setName] = useState("");
    const [isHeading, setIsHeading] = useState(false);
    const [perParty, setPerParty] = useState(false);
    const [fixedParty, setFixedParty] = useState("");
    const [contra, setContra] = useState(false);
    const [cf, setCf] = useState("operating");
    const [desc, setDesc] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const parent = data.accounts.find(a => a.id === parentId);
    useEffect(() => {
        if (!open) return;
        setParentId(parentDefault?.id || ""); setName(""); setIsHeading(false); setPerParty(false); setFixedParty("");
        setContra(false); setDesc(""); setErr(""); setBusy(false);
        setCf(parentDefault?.type === "equity" ? "financing" : "operating");
        setCode(parentDefault ? nextFreeCode(data.accounts, parentDefault) : "");
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
    const save = async () => {
        setErr("");
        if (!parent) { setErr("Choose where the account belongs."); return; }
        if (!/^\d{4}$/.test(code)) { setErr("The code must be 4 digits."); return; }
        if (!name.trim()) { setErr("Enter a name."); return; }
        setBusy(true);
        try {
            await createAccount({ code, name: name.trim(), type: parent.type, parentCode: parent.code, isPostable: !isHeading, isContra: contra,
                requiresParty: perParty || !!fixedParty, fixedPartyId: fixedParty, cashFlowClass: isHeading ? null : cf, description: desc.trim() });
            showToast(`Account ${code} added`);
            await onDone();
        } catch (e) { setErr(errText(e)); setBusy(false); }
    };
    return (
        <Modal open={open} title="Add account" subtitle={parent ? `${TYPE_INFO[parent.type][0]} · under ${parent.code} ${parent.name}` : undefined} onClose={busy ? undefined : onClose}
            footer={<><Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button><Button onClick={save} disabled={busy}>{busy ? "Saving…" : "Add account"}</Button></>}>
            <div style={{ display: "grid", gap: 14 }}>
                <Field label="Belongs under" required>
                    <Select value={parentId} onChange={e => { setParentId(e.target.value); const p = data.accounts.find(a => a.id === e.target.value); if (p) setCode(nextFreeCode(data.accounts, p)); }}>
                        <option value="">Choose a heading…</option>
                        {headers.map(h => <option key={h.id} value={h.id}>{h.code} {h.name}</option>)}
                    </Select>
                </Field>
                <div style={{ display: "grid", gridTemplateColumns: "110px 1fr", gap: 10 }}>
                    <Field label="Code" required><Input value={code} inputMode="numeric" maxLength={4} onChange={e => setCode(e.target.value.replace(/\D/g, ""))} /></Field>
                    <Field label="Name" required><Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Bank – Second account" /></Field>
                </div>
                <Field label="What is it for?">
                    <Select value={isHeading ? "heading" : "posting"} onChange={e => setIsHeading(e.target.value === "heading")}>
                        <option value="posting">Records amounts (used in entries)</option>
                        <option value="heading">Groups other accounts (heading only)</option>
                    </Select>
                </Field>
                {!isHeading && <>
                    <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 14, color: T.text }}>
                        <input type="checkbox" checked={perParty || !!fixedParty} disabled={!!fixedParty} onChange={e => setPerParty(e.target.checked)} style={{ marginTop: 3 }} />
                        <span>Track balances per person or company<div style={text.caption}>For receivables, payables, advances and loans. Every entry must name who it is for.</div></span>
                    </label>
                    <Field label="Only for one person or company (optional)" hint="For example a shareholder's own loan account. Entries for anyone else are refused.">
                        <Select value={fixedParty} onChange={e => setFixedParty(e.target.value)}>
                            <option value="">Anyone</option>
                            {data.parties.filter(p => p.active).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </Select>
                    </Field>
                    <Field label="Cash-flow statement group">
                        <Select value={cf} onChange={e => setCf(e.target.value)}>{CASH_FLOW.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</Select>
                    </Field>
                    <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 14, color: T.text }}>
                        <input type="checkbox" checked={contra} onChange={e => setContra(e.target.checked)} style={{ marginTop: 3 }} />
                        <span>Reduces its group<div style={text.caption}>Rare. For example Accumulated Depreciation, which reduces the value of equipment.</div></span>
                    </label>
                </>}
                <Field label="Description"><TextArea value={desc} onChange={e => setDesc(e.target.value)} placeholder="Optional — what should be recorded here" /></Field>
                {err && <Notice tone="danger">{err}</Notice>}
            </div>
        </Modal>
    );
}

function EditAccountModal({ account, onClose, onDone }) {
    const [name, setName] = useState("");
    const [desc, setDesc] = useState("");
    const [cf, setCf] = useState("");
    const [active, setActive] = useState(true);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    useEffect(() => {
        if (account) { setName(account.name); setDesc(account.description || ""); setCf(account.cash_flow_class || ""); setActive(account.active); setErr(""); setBusy(false); }
    }, [account]);
    if (!account) return null;
    const save = async () => {
        if (!name.trim()) { setErr("Enter a name."); return; }
        setBusy(true); setErr("");
        try {
            await updateAccount(account.id, { name: name.trim(), active, cashFlowClass: account.is_postable ? cf : null, description: desc.trim() });
            showToast(`Account ${account.code} updated`);
            await onDone();
        } catch (e) { setErr(errText(e)); setBusy(false); }
    };
    return (
        <Modal open title={`Edit ${account.code}`} subtitle={TYPE_INFO[account.type][0]} onClose={busy ? undefined : onClose}
            footer={<><Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button><Button onClick={save} disabled={busy}>{busy ? "Saving…" : "Save"}</Button></>}>
            <div style={{ display: "grid", gap: 14 }}>
                <Field label="Name" required><Input value={name} onChange={e => setName(e.target.value)} /></Field>
                {account.is_postable && (
                    <Field label="Cash-flow statement group">
                        <Select value={cf} onChange={e => setCf(e.target.value)}>{CASH_FLOW.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</Select>
                    </Field>
                )}
                <Field label="Description"><TextArea value={desc} onChange={e => setDesc(e.target.value)} /></Field>
                {account.is_system
                    ? <Notice tone="info">Built-in account: it stays active and keeps its code and type because automatic postings rely on it.</Notice>
                    : <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 14, color: T.text }}>
                        <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} /> Active (inactive accounts can't be used in new entries; history is kept)
                    </label>}
                {err && <Notice tone="danger">{err}</Notice>}
            </div>
        </Modal>
    );
}
